import React, { useEffect, useMemo, useState, useRef } from "react";
import ProfileDataList from "./ProfileDataList";
import { Modal, Form, Spin, Input, Select, Divider, message, Tooltip, TreeSelect } from "antd";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../State/Global/AppContext";
import LoadMore from "../LoadMore/LoadMore";
import { sortByorder, convertArrayToObject, convertObjectToArray } from "./Helpers";
import { api } from "../../Requests/Api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import {
    IonContent,
    IonHeader,
    IonModal,
    IonTitle,
    IonButton,
    IonButtons,
    IonToolbar,
} from "@ionic/react";
import { isMobileView, isNativeMobile } from "frontend-lib/Utils/Mobile";
import { useNavigate } from "react-router-dom";
import ConvTagTreeSelect from "./ConvTagTreeSelect";

const { Option } = Select;

function ModalProfileData({
    onCloseCb,
    customer,
    editCustomerCb,
    visible,
    editCustomer,
    isLoading,
    customerFields,
    convTags,
    convTagGroups,
}) {
    const { appState } = useAppContext();
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const nativeKeys = ["fullName", "email", "phone", "notes"];
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [allAgents, setAllAgents] = useState([]);
    const navigate = useNavigate();
    const [agentsPaginator, setAgentsPaginator] = useState({
        page: 0,
        hasNext: false,
        amout: 0,
    });
    const currentAgent = appState.agent;
    const disableAffinity = useMemo(() => {
        return appState.workspaceConfig?.disableAffinity !== undefined
            ? appState.workspaceConfig.disableAffinity
            : true;
    }, [appState.workspaceConfig.disableAffinity]);

    const handleFormFinish = function (data) {};

    const closeModal = function () {
        form.resetFields();
        resetAgents();
        onCloseCb();
    };
    const handleFormSubmit = function () {
        if (!editCustomer) {
            form.resetFields();
            onCloseCb();
            return;
        }
        form.validateFields()
            .then((val) => {
                const editedNativeFieldsList = [];
                const editedExtraDataList = [];
                for (let item of convertObjectToArray(val)) {
                    const key = Object.keys(item)[0];
                    if (customerFields.length > 0) {
                        const customerField = customerFields.find((item) => item.key === key);
                        if (nativeKeys.includes(key) && customerField && customerField.isNative) {
                            editedNativeFieldsList.push(item);
                        } else {
                            editedExtraDataList.push(item);
                        }
                    } else {
                        editedNativeFieldsList.push(item);
                    }
                }
                const editedNativeFields = convertArrayToObject(editedNativeFieldsList);
                const editedExtraData = convertArrayToObject(editedExtraDataList);
                const editedCustomer = {
                    ...customer,
                    ...editedNativeFields,
                    extraData: { ...customer.extraData, ...editedExtraData },
                    ...(selectedAgent
                        ? { affinityWithUserId: selectedAgent.id, affinityAgent: selectedAgent }
                        : { clearAffinity: true, affinityAgent: null, affinityWithUserId: null }),
                };
                resetAgents();
                editCustomerCb(editedCustomer);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    async function resetAgents() {
        setAllAgents([]);
        setAgentsPaginator({
            page: 0,
            hasNext: false,
            amout: 0,
        });
        setSelectedAgent(null);
    }

    function uniqBy(array, key) {
        let seen = new Set();
        return array.filter((item) => {
            let k = key(item);
            return seen.has(k) ? false : seen.add(k);
        });
    }

    async function loadAgents(paginator) {
        try {
            const res = await api.users().get(null, paginator.page);
            const users = res.data.users;
            const new_paginator = res.data.paginator;

            setAgentsPaginator(new_paginator);
            const mergedAgents = uniqBy([...allAgents, ...users], (a) => a.id);
            setAllAgents(mergedAgents);
            if (customer.affinityAgent) {
                const agent = customer.affinityAgent;
                setSelectedAgent(agent);
            }
        } catch (e) {
            console.log(e);
        }
    }

    async function loadMoreAgents() {
        if (agentsPaginator.hasNext) {
            await loadAgents({ ...agentsPaginator, page: agentsPaginator.nextPage });
        }
    }

    function onSelectAffinityAgent(agentLabel) {
        if (selectedAgent == null || currentAgent.isAdmin) {
            const newAffinityAgent = allAgents.find((a) => a.id === agentLabel.value);
            if (!currentAgent.isAdmin && newAffinityAgent.id !== currentAgent.id) {
                message.error(t("clientForm.errorAffinity"));
                return;
            }
            setSelectedAgent(newAffinityAgent);
        } else {
            message.error(t("clientForm.errorAffinity"));
        }
    }

    async function onClearAffinity() {
        if (currentAgent.isAdmin || customer.affinityAgent.id === currentAgent.id) {
            setSelectedAgent(null);
        } else {
            message.error(t("clientForm.errorAffinity"));
        }
    }

    useEffect(() => {
        form.resetFields();
        resetAgents();
        if (visible === true) {
            loadAgents(agentsPaginator);
        }

        if (customer) {
            const customerForm = {};
            for (const key in customer) {
                if (nativeKeys.includes(key)) {
                    customerForm[key] = customer[key];
                }
            }
            for (const key in customer.extraData) {
                customerForm[key] = customer.extraData[key];
            }
            form.setFieldsValue(customerForm);
        }
    }, [customer, visible, customerFields]);

    const wrapInAntdModal = (children) => {
        return (
            <Modal
                visible={visible}
                title={t("info")}
                onCancel={closeModal}
                onOk={handleFormSubmit}
                bodyStyle={{ maxHeight: "400px", overflow: "scroll" }}
            >
                {children}
            </Modal>
        );
    };

    const ionModal = useRef(null);

    const wrapInIonicModal = (children) => {
        return (
            <IonModal isOpen={visible} ref={ionModal}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>{t("chatInfoTitle")}</IonTitle>{" "}
                        <IonButtons slot="start">
                            <IonButton onClick={() => onCloseCb()}>{t("close")}</IonButton>
                        </IonButtons>
                        <IonButtons slot="end">
                            <IonButton
                                onClick={() => {
                                    handleFormSubmit && handleFormSubmit();
                                    ionModal.current?.dismiss();
                                    navigate(-1);
                                }}
                            >
                                {t("save")}
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <div style={{ padding: "3rem 1rem 0" }}>{children}</div>
                    {/* This div fixes a bug in ios where the fields at the bottom of the page cannot be reached */}
                    <div style={{ height: "90%" }}></div>
                </IonContent>
            </IonModal>
        );
    };

    const renderFormBody = () => {
        return (
            <Form.Provider onFormFinish={handleFormFinish}>
                <Spin spinning={isLoading}>
                    {editCustomer ? (
                        <>
                            <Form form={form} layout="vertical" onFinish={handleFormFinish}>
                                {customerFields.length > 0 &&
                                    customerFields.sort(sortByorder).map((item) => (
                                        <>
                                            <Form.Item
                                                key={item.key}
                                                label={
                                                    item.isNative
                                                        ? t(`clientForm.${item.key}`)
                                                        : item.label
                                                }
                                                name={item.key}
                                                rules={
                                                    item.key === "fullName" &&
                                                    customer &&
                                                    customer[item.key] && [
                                                        { required: true, message: t("required") },
                                                    ]
                                                }
                                            >
                                                {item.key === "notes" ? (
                                                    <Input.TextArea
                                                        placeholder={t("clientForm.notes")}
                                                    />
                                                ) : (
                                                    <Input placeholder={`${item.label}`} />
                                                )}
                                            </Form.Item>
                                        </>
                                    ))}
                                {customerFields.length === 0 &&
                                    ["fullName", "email", "phone", "notes"].map((key) => (
                                        <>
                                            <Form.Item
                                                key={key}
                                                label={t(`clientForm.${key}`)}
                                                name={key}
                                                rules={
                                                    key === "fullName" &&
                                                    customer &&
                                                    customer[key] && [
                                                        { required: true, message: "Required" },
                                                    ]
                                                }
                                            >
                                                {key === "notes" ? (
                                                    <Form.Item label={t("Notes")} name={"notes"}>
                                                        <Input.TextArea
                                                            placeholder={t("clientForm.notes")}
                                                        />
                                                    </Form.Item>
                                                ) : (
                                                    <Input placeholder={`Customer ${key}`} />
                                                )}
                                            </Form.Item>
                                        </>
                                    ))}
                                <Form.Item>
                                    {t("clientForm.affinityAgent")}
                                    <Tooltip
                                        title={
                                            t("clientForm.affinityTooltip") +
                                            (disableAffinity
                                                ? `\n${t("clientForm.affinityDisabled")}`
                                                : "")
                                        }
                                    >
                                        <FontAwesomeIcon
                                            style={{ fontSize: "10px", paddingLeft: "2px" }}
                                            icon={faQuestionCircle}
                                        />
                                    </Tooltip>
                                    <Select
                                        value={
                                            selectedAgent
                                                ? {
                                                      value: selectedAgent.id,
                                                      label: `${selectedAgent.firstName} ${selectedAgent.lastName}`,
                                                  }
                                                : { value: "", label: "" }
                                        }
                                        labelInValue
                                        onSelect={onSelectAffinityAgent}
                                        disabled={disableAffinity}
                                        allowClear
                                        onClear={onClearAffinity}
                                        popupClassName="selectPopUpMobile"
                                        dropdownRender={(menu) => (
                                            <>
                                                {menu}
                                                {agentsPaginator.hasNext && (
                                                    <>
                                                        <Divider />
                                                        <LoadMore onClick={loadMoreAgents} />
                                                    </>
                                                )}
                                            </>
                                        )}
                                    >
                                        {currentAgent.isAdmin
                                            ? allAgents.map((a) => (
                                                  <Option key={a.id} value={a.id}>
                                                      {a.firstName} {a.lastName}
                                                  </Option>
                                              ))
                                            : allAgents
                                                  .filter((a) => a.id === currentAgent.id)
                                                  .map((a) => (
                                                      <Option key={a.id} value={a.id}>
                                                          {a.firstName} {a.lastName}
                                                      </Option>
                                                  ))}
                                    </Select>
                                </Form.Item>
                            </Form>
                            {isMobileView() ? (
                                <>
                                    <span>{t("labels.conversationLabel")}</span>
                                    <ConvTagTreeSelect
                                        convTags={convTags}
                                        convTagsGroups={convTagGroups}
                                    />
                                </>
                            ) : (
                                <></>
                            )}
                        </>
                    ) : (
                        <div className="profile-box">
                            <ProfileDataList
                                customer={customer}
                                customerFields={customerFields}
                                showAll={true}
                            />
                        </div>
                    )}
                </Spin>
            </Form.Provider>
        );
    };

    if (isMobileView()) {
        return wrapInIonicModal(renderFormBody());
    } else {
        return wrapInAntdModal(renderFormBody());
    }
}
export default ModalProfileData;
